<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useChatbotsStore } from 'dashboard/stores/chatbots';

import Button from 'dashboard/components-next/button/Button.vue';
import ChatbotCard from 'dashboard/components-next/Chatbots/ChatbotCard.vue';
import ChatbotCreateDialog from 'dashboard/components-next/Chatbots/ChatbotCreateDialog.vue';

const chatbotsStore = useChatbotsStore();
const { t } = useI18n();

const chatbots = computed(() => chatbotsStore.list);

const dialogRef = ref(null);

const openCreateDialog = () => {
  dialogRef.value?.openForCreate();
};

const onEdit = chatbot => {
  dialogRef.value?.openForEdit(chatbot);
};

const onSubmit = async chatbot => {
  try {
    if (chatbot.id) {
      const updated = await chatbotsStore.update(chatbot.id, chatbot);
      dialogRef.value?.onSuccess();
      useAlert(t('CHATBOTS.ALERTS.UPDATED', { name: updated.botName }));
    } else {
      const created = await chatbotsStore.create(chatbot);
      dialogRef.value?.onSuccess();
      useAlert(t('CHATBOTS.ALERTS.CREATED', { name: created.botName }));
    }
  } catch (error) {
    useAlert(t('CHATBOTS.ALERTS.ERROR'));
  }
};

const onToggle = id => {
  chatbotsStore.toggleActive(id);
};

const onRemove = id => {
  chatbotsStore.remove(id);
};

onMounted(() => {
  chatbotsStore.fetch();
});
</script>

<template>
  <div class="flex w-full h-full overflow-hidden bg-n-surface-1">
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full mx-auto max-w-5xl py-6 flex flex-col gap-6">
        <!-- Header -->
        <div class="flex items-center justify-between gap-4">
          <div class="flex flex-col">
            <h1 class="text-xl font-semibold text-n-slate-12 m-0">
              {{ t('CHATBOTS.INDEX.TITLE') }}
            </h1>
            <span class="text-sm text-n-slate-11">
              {{ t('CHATBOTS.INDEX.SUBTITLE') }}
            </span>
          </div>
          <Button
            :label="t('CHATBOTS.INDEX.NEW')"
            color="blue"
            icon="i-lucide-plus"
            @click="openCreateDialog"
          />
        </div>

        <!-- Empty state -->
        <div
          v-if="chatbots.length === 0"
          class="flex flex-col items-center justify-center gap-3 py-16 rounded-xl border border-dashed border-n-weak"
        >
          <span class="i-lucide-bot size-10 text-n-slate-9" />
          <span class="text-base text-n-slate-11">
            {{ t('CHATBOTS.INDEX.EMPTY') }}
          </span>
          <Button
            :label="t('CHATBOTS.INDEX.CREATE_FIRST')"
            variant="outline"
            icon="i-lucide-plus"
            @click="openCreateDialog"
          />
        </div>

        <!-- Chatbots grid -->
        <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ChatbotCard
            v-for="chatbot in chatbots"
            :key="chatbot.id"
            :chatbot="chatbot"
            @toggle="onToggle"
            @remove="onRemove"
            @edit="onEdit"
          />
        </div>
      </div>
    </main>

    <ChatbotCreateDialog ref="dialogRef" @submit="onSubmit" />
  </div>
</template>
